import { Capacitor } from '@capacitor/core';

/**
 * The web SPA calls APIs with relative paths (`/api/*`) on the
 * assumption that the page was loaded from the API origin. That holds
 * on the browser, and in production where the API and the SPA share
 * an origin — but it breaks inside the Capacitor WebView, which loads
 * the bundle from `https://localhost` (Android) or `capacitor://localhost`
 * (iOS). Relative fetches then resolve to localhost, which has no
 * server, so every API call hangs/fails — including sign-in.
 *
 * Fix: when running native, patch `window.fetch` and `Request` so any
 * URL starting with `/` is prepended with the API origin. CapacitorHttp
 * (enabled in capacitor.config.ts) then routes the call at the native
 * layer, bypassing the WebView's CORS / SameSite rules and storing
 * cookies in the OS cookie jar against lessalone.app.
 *
 * Call `installNativeApiOrigin()` ONCE, very early — before any module
 * is loaded that might issue a fetch (Better-Auth at import time, the
 * lib/api helpers, etc.). main.tsx imports this file at the top.
 */

/**
 * The remote API origin used when the WebView is shelled inside
 * Capacitor. Override at build time with `VITE_NATIVE_API_ORIGIN` so
 * a future staging build can point at a non-prod URL. Defaults to the
 * production origin since the dogfood APKs always talk to prod.
 */
export const NATIVE_API_ORIGIN: string =
  (import.meta.env.VITE_NATIVE_API_ORIGIN as string | undefined) ?? 'https://lessalone.app';

/**
 * Same multi-signal native detection as lib/capacitor.ts → isNative().
 * Duplicated here because this module loads BEFORE the rest of the
 * SPA (main.tsx imports it first to patch fetch); we want to keep
 * its imports minimal.
 */
export function isNativeShell(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (Capacitor.isNativePlatform?.()) return true;
    const platform = Capacitor.getPlatform?.();
    if (platform === 'ios' || platform === 'android') return true;
  } catch {
    // Capacitor module not present.
  }
  const cap = (window as unknown as { Capacitor?: { isNative?: boolean; platform?: string } })
    .Capacitor;
  if (cap?.isNative === true) return true;
  if (cap?.platform === 'ios' || cap?.platform === 'android') return true;
  return false;
}

/**
 * Resolve a request URL against the API origin when running native.
 * Relative paths get prepended; absolute URLs and same-origin URLs are
 * passed through. Safe to call on web — it's a no-op.
 */
export function resolveApiUrl(input: string): string {
  if (!isNativeShell()) return input;
  if (input.startsWith('/') && !input.startsWith('//')) {
    return `${NATIVE_API_ORIGIN}${input}`;
  }
  return input;
}

let installed = false;

/**
 * Patch window.fetch to rewrite relative API paths to absolute lessalone.app
 * URLs when running in the Capacitor shell. Idempotent — calling it more
 * than once is a no-op (matters for hot reload during dev).
 */
/** True when a (resolved) URL targets our own API origin. */
function isApiTarget(url: string): boolean {
  return url.startsWith(NATIVE_API_ORIGIN);
}

/**
 * Merge the native tag header into a fetch init, but ONLY for calls to our
 * own API. The server uses `X-LessAlone-Native: 1` as a forgery-resistant
 * "this is the Capacitor shell" signal to satisfy Better-Auth's CSRF origin
 * check (native CapacitorHttp requests carry no Origin header). We must NOT
 * add it to third-party requests (Sentry, Google Fonts) — a custom header
 * there would trip their CORS preflight and break the call.
 */
function tagNativeInit(url: string, init?: RequestInit): RequestInit | undefined {
  if (!isApiTarget(url)) return init;
  const headers = new Headers(init?.headers);
  headers.set('X-LessAlone-Native', '1');
  return { ...init, headers };
}

export function installNativeApiOrigin(): void {
  if (installed) return;
  if (!isNativeShell()) return;
  installed = true;
  const origFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    if (typeof input === 'string') {
      const url = resolveApiUrl(input);
      return origFetch(url, tagNativeInit(url, init));
    }
    if (input instanceof URL) {
      const url = input.pathname.startsWith('/')
        ? resolveApiUrl(input.pathname + input.search + input.hash)
        : input.toString();
      return origFetch(url, tagNativeInit(url, init));
    }
    if (input instanceof Request) {
      const resolved = resolveApiUrl(input.url);
      if (!isApiTarget(resolved)) {
        // Third-party (or already-correct) request — pass through, only
        // rebuilding if the URL actually changed.
        return resolved === input.url ? origFetch(input, init) : origFetch(new Request(resolved, input), init);
      }
      // First-party API call: rebuild with the (possibly rewritten) URL and
      // add the native tag header.
      const rebuilt = new Request(resolved, input);
      rebuilt.headers.set('X-LessAlone-Native', '1');
      return origFetch(rebuilt, init);
    }
    return origFetch(input as RequestInfo, init);
  };
}
